import { createServerFn } from "@tanstack/react-start"
import { getRequest } from "@tanstack/react-start/server"
import { and, count, desc, eq, gte, isNotNull, like, lte, sql } from "drizzle-orm"
import { db } from "@/db/db"
import { auditEvents } from "@/db/schema"
import {
  auditLogQuerySchema,
  type AuditLogEntry,
  type AuditLogResponse,
} from "@/features/audit-logs/types/auditTypes"
import { assertPermission, PermissionError } from "@/lib/assert-permission.server"
import { authFunctionMiddleware } from "@/lib/auth-middleware"

// Re-export types for backwards compatibility
export {
  auditLogQuerySchema,
  type AuditLogEntry,
  type AuditLogFilters,
  type AuditLogResponse,
} from "@/features/audit-logs/types/auditTypes"

function parseDate(value?: string) {
  if (!value) return undefined
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? undefined : parsed
}

/**
 * Escape LIKE pattern metacharacters so a user-supplied value is treated as
 * a literal prefix rather than a pattern. Without this, inputs like "%" or
 * "_" would bypass the intended prefix-only semantics and match anything.
 */
function escapeLikePrefix(value: string): string {
  return value.replace(/\\/g, "\\\\").replace(/%/g, "\\%").replace(/_/g, "\\_")
}

export const getAuditLogs = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .inputValidator((filters: unknown) => auditLogQuerySchema.parse(filters))
  .handler(async ({ data: filters }): Promise<AuditLogResponse> => {
    // Check permission - only admin and blueprint_planner can read audit logs
    const request = getRequest()
    try {
      await assertPermission(request, "auditLog", "read")
    } catch (error) {
      if (error instanceof PermissionError) {
        return {
          success: false,
          error: error.message,
          code: error.code,
        }
      }
      throw error
    }

    const { pageIndex, pageSize } = filters
    const offset = pageIndex * pageSize
    const whereConditions = []

    const fromDate = parseDate(filters.from)
    const toDate = parseDate(filters.to)

    if (filters.scopeId) whereConditions.push(eq(auditEvents.scopeId, filters.scopeId))
    if (filters.userId) whereConditions.push(eq(auditEvents.actorId, filters.userId))
    if (filters.entityType) whereConditions.push(eq(auditEvents.entityType, filters.entityType))
    if (filters.entityId) whereConditions.push(eq(auditEvents.entityId, filters.entityId))
    if (filters.action)
      whereConditions.push(like(auditEvents.action, `${escapeLikePrefix(filters.action)}%`))
    if (fromDate) whereConditions.push(gte(auditEvents.occurredAt, fromDate))
    if (toDate) whereConditions.push(lte(auditEvents.occurredAt, toDate))

    const whereClause = whereConditions.length ? and(...whereConditions) : undefined

    const rawEvents = await db.query.auditEvents.findMany({
      where: whereClause,
      orderBy: [desc(auditEvents.occurredAt)],
      limit: pageSize,
      offset,
    })

    const events: AuditLogEntry[] = rawEvents.map((event) => ({
      ...event,
      entityName: event.entityName ?? null,
      metadata: event.metadata ?? null,
      diff: event.diff ?? null,
    }))

    const totalCountResult = await db
      .select({ count: count() })
      .from(auditEvents)
      .where(whereClause)

    const filterOptions = await Promise.all([
      db
        .select({ scopeId: auditEvents.scopeId })
        .from(auditEvents)
        .where(isNotNull(auditEvents.scopeId))
        .groupBy(auditEvents.scopeId),
      db
        .select({
          actorId: auditEvents.actorId,
          actorDisplay: sql<string>`max(${auditEvents.actorDisplay})`,
        })
        .from(auditEvents)
        .where(isNotNull(auditEvents.actorId))
        .groupBy(auditEvents.actorId),
      db
        .select({ entityType: auditEvents.entityType })
        .from(auditEvents)
        .where(isNotNull(auditEvents.entityType))
        .groupBy(auditEvents.entityType),
    ])

    const [scopes, users, entityTypes] = filterOptions

    return {
      success: true,
      events,
      totalCount: totalCountResult[0]?.count ?? 0,
      filterOptions: {
        scopes: scopes.map((scope) => scope.scopeId),
        users: users.map((user) => ({
          id: user.actorId,
          display: user.actorDisplay,
        })),
        entityTypes: entityTypes
          .map((type) => type.entityType)
          .filter((value): value is string => Boolean(value)),
      },
    }
  })
